import React, { useEffect, useRef } from 'react';

import { ReceivedSoundArrType } from './QueuePanel';
import { baseSoundURL } from '../../../config';
import {
  getSharedTabloAudioContext,
  unlockTabloMedia
} from '../../../hooks/MonitorQueue/useTabloKioskBootstrap';
import { isAndroidTabloDevice } from '../../../utils/QueuePage/tabloDevice';

interface SoundPlayerProps {
  soundsData?: ReceivedSoundArrType | null;
  updateSoundStatus: (id: number | null) => void;
  setIsSoundPlaying: (status: boolean) => void;
  onPlayFailed?: () => void;
}

type CancelFlag = { cancelled: boolean };

const preferHtmlAudio = isAndroidTabloDevice();

const toUrl = (path: string) => {
  if (!path) return path;
  if (/^https?:\/\//i.test(path) || path.startsWith('//') || path.startsWith('blob:')) {
    return path;
  }
  const base = baseSoundURL.replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  return `${base}${rel}`;
};

/**
 * HTMLAudio по прямому URL — без fetch/CORS.
 * На WebView 86 / Fully это самый надёжный путь.
 */
const playWithHtmlAudioDirect = async (paths: string[], flag: CancelFlag) => {
  let played = 0;
  for (const path of paths) {
    if (flag.cancelled) break;
    const url = toUrl(path);
    const ok = await new Promise<boolean>((resolve) => {
      const audio = new Audio();
      let settled = false;
      const finish = (success: boolean) => {
        if (settled) return;
        settled = true;
        audio.onended = null;
        audio.onerror = null;
        audio.onloadedmetadata = null;
        try {
          audio.pause();
          audio.removeAttribute('src');
          audio.load();
        } catch {
          // ignore
        }
        resolve(success);
      };

      audio.preload = 'auto';
      audio.src = url;
      audio.onended = () => finish(true);
      audio.onerror = () => finish(false);
      audio.onloadedmetadata = () => {
        if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
        window.setTimeout(() => finish(true), audio.duration * 1000 + 400);
      };

      const tryPlay = () => {
        const p = audio.play();
        if (p && typeof p.then === 'function') {
          p.catch(() => finish(false));
        }
      };

      // WebView: play сразу и ещё раз после canplay
      audio.oncanplay = () => {
        audio.oncanplay = null;
        tryPlay();
      };
      tryPlay();
    });
    if (ok) played += 1;
  }
  if (!played && paths.length) {
    throw new Error('HTMLAudio playback failed');
  }
};

const fetchAndDecode = async (
  ctx: AudioContext,
  path: string
): Promise<AudioBuffer | null> => {
  try {
    const response = await fetch(toUrl(path), {
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache'
    });
    if (!response.ok) return null;
    const data = await response.arrayBuffer();
    // copy — decodeAudioData может «отцепить» буфер
    const copy = data.slice(0);
    return await new Promise<AudioBuffer>((resolve, reject) => {
      // Старые WebView: decodeAudioData(buf, success, error); новые — Promise
      let settled = false;
      const ok = (buf: AudioBuffer) => {
        if (settled) return;
        settled = true;
        resolve(buf);
      };
      const fail = (err?: DOMException) => {
        if (settled) return;
        settled = true;
        reject(err || new Error('decodeAudioData failed'));
      };
      try {
        const maybePromise = ctx.decodeAudioData(copy, ok, fail) as Promise<AudioBuffer> | void;
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise.then(ok, fail);
        }
      } catch (err) {
        fail(err as DOMException);
      }
    });
  } catch {
    return null;
  }
};

const playWithWebAudio = async (paths: string[], flag: CancelFlag) => {
  await unlockTabloMedia();
  const ctx = getSharedTabloAudioContext();
  if (!ctx) {
    throw new Error('AudioContext unavailable');
  }
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  if (ctx.state !== 'running') {
    throw new Error('AudioContext not running');
  }

  const buffers = await Promise.all(paths.map((path) => fetchAndDecode(ctx, path)));
  if (flag.cancelled) return;

  const valid = buffers.filter((b): b is AudioBuffer => !!b && b.duration > 0);
  if (!valid.length) {
    throw new Error('No decodable audio clips');
  }

  const totalDuration = valid.reduce((sum, buffer) => sum + buffer.duration, 0);
  let startAt = ctx.currentTime + 0.05;
  const sources: AudioBufferSourceNode[] = [];

  valid.forEach((buffer) => {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(startAt);
    sources.push(source);
    startAt += buffer.duration;
  });

  const playMs = Math.ceil(totalDuration * 1000) + 500;
  await new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, playMs);
    const cancelWatch = window.setInterval(() => {
      if (!flag.cancelled) return;
      window.clearTimeout(timer);
      window.clearInterval(cancelWatch);
      sources.forEach((s) => {
        try {
          s.stop();
        } catch {
          // already stopped
        }
      });
      resolve();
    }, 150);
    window.setTimeout(() => window.clearInterval(cancelWatch), playMs + 50);
  });
};

const playAnnouncement = async (paths: string[], flag: CancelFlag) => {
  if (preferHtmlAudio) {
    try {
      await playWithHtmlAudioDirect(paths, flag);
      return;
    } catch {
      if (flag.cancelled) return;
      await playWithWebAudio(paths, flag);
      return;
    }
  }
  try {
    await playWithWebAudio(paths, flag);
  } catch {
    if (flag.cancelled) return;
    await playWithHtmlAudioDirect(paths, flag);
  }
};

const SoundPlayer: React.FC<SoundPlayerProps> = ({
  soundsData,
  updateSoundStatus,
  setIsSoundPlaying,
  onPlayFailed
}) => {
  const playingRef = useRef(false);
  const updateSoundStatusRef = useRef(updateSoundStatus);
  const setIsSoundPlayingRef = useRef(setIsSoundPlaying);
  const onPlayFailedRef = useRef(onPlayFailed);
  updateSoundStatusRef.current = updateSoundStatus;
  setIsSoundPlayingRef.current = setIsSoundPlaying;
  onPlayFailedRef.current = onPlayFailed;

  useEffect(() => {
    if (!soundsData?.sounds?.length) return;
    if (playingRef.current) return;

    const flag: CancelFlag = { cancelled: false };
    const phrases = [...soundsData.sounds].reverse();
    const ids = [...(soundsData.receivedSoundsIds ?? [])].reverse();
    const playSoundsArr: string[] = [];

    phrases.forEach((clips) => {
      playSoundsArr.push(...clips);
    });

    if (!playSoundsArr.length) return;

    playingRef.current = true;
    setIsSoundPlayingRef.current(true);
    void unlockTabloMedia();

    const run = async () => {
      let lastError: unknown;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (flag.cancelled) return;
        try {
          await playAnnouncement(playSoundsArr, flag);
          if (flag.cancelled) return;
          // Помечаем сыгранным только после реального воспроизведения,
          // иначе следующий poll отменяет play и звук «пропадает».
          ids.forEach((id) => {
            if (id != null) updateSoundStatusRef.current(id);
          });
          return;
        } catch (err) {
          lastError = err;
          await new Promise((r) => window.setTimeout(r, 800));
        }
      }
      if (lastError) {
        // не помечаем played — сбросим signature, чтобы poll мог повторить
        console.warn('Tablo announcement failed', lastError);
        onPlayFailedRef.current?.();
      }
    };

    void run().finally(() => {
      playingRef.current = false;
      if (!flag.cancelled) {
        setIsSoundPlayingRef.current(false);
      }
    });

    return () => {
      // Не отменяем текущее объявление при смене ссылки на soundsData —
      // иначе poll во время play глушит звук. playingRef защищает от ре-энтри.
    };
  }, [soundsData]);

  return null;
};

export default SoundPlayer;
